import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  getUnixTime,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { CalendarDateFormatter, CalendarEvent, CalendarView, collapseAnimation } from '@gallaghercp/core-calendar';
import { AuthService, BlockAbstractComponent, CacheService, ParserService } from '@gallaghercp/core-frontend';
import { map, tap } from 'rxjs/operators';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { CustomDateFormatter } from '@core-app/client/providers/custom-date-formatter.provider';
import { CampusService } from '@core-app/services/campus.service';
import { CampusType } from '@core-app/client/types/campus.type';
import { countBy } from 'lodash';
import { WeekDay } from '@gallaghercp/core-calendar/modules/calendar-utils/calendar-utils';
import { HttpClient } from '@angular/common/http';
import { toZonedTime } from 'date-fns-tz';

interface CalendarAppEvent extends CalendarEvent {
  title: string;
  description?: string;
  type?: string;
}

interface EventType {
  type: string;
  active: boolean;
}

const colors: any = {
  holiday: {
    primary: 'var(--color-primary)', // border
    secondary: 'var(--color-primary)', // background
  },
  event: {
    primary: 'var(--color-primary)', // border
    secondary: 'var(--color-secondary)', // background
  },
};

@Component({
  selector: 'core-block-calendar',
  templateUrl: './block-calendar.component.html',
  styleUrls: ['./block-calendar.component.scss'],
  standalone: false,
  providers: [
    {
      provide: CalendarDateFormatter,
      useClass: CustomDateFormatter,
    },
  ],
  animations: [collapseAnimation],
})
export class BlockCalendarComponent extends BlockAbstractComponent implements OnInit, OnDestroy {
  protected override parser: ParserService;
  protected override auth: AuthService;
  protected http = inject(HttpClient);
  private cache = inject(CacheService);
  private campus = inject(CampusService);
  private breakpointObserver = inject(BreakpointObserver);
  private cdr = inject(ChangeDetectorRef);
  private modalService = inject(BsModalService);

  @ViewChild('calendarMain') calendarMain?: ElementRef;
  @ViewChild('eventModal') eventModal?: TemplateRef<any>;

  view: CalendarView = CalendarView.Month;
  CalendarView = CalendarView;
  userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  viewDate = toZonedTime(new Date(), this.userTimeZone);
  modalRef!: BsModalRef;
  modalData?: {
    action: string;
    event: CalendarAppEvent;
  };
  refresh = new Subject<any>();
  events$!: Observable<CalendarAppEvent[]>;
  eventTypes!: EventType[];

  calendarType = 'public-holidays'; // @TODO: make block variable
  daysInWeek = 7;
  sidebarToggleStatus = false;
  isCalendarOffCanvasAllowed = false;

  toggleCalendarOffCanvas = false;
  sidebarElWidth?: number;
  isCustomMonthEventView = true; // Use custom event styling rather than popover
  isCustomMonthEventViewForMobile = false; // Mobile used month view but styled differently
  activeDayIsOpen = false;
  protected readonly countBy = countBy;
  private destroy$: Subject<void> = new Subject<void>();

  expandedDays!: number[];
  isAllToggled = false;
  downloading?: boolean = false;

  constructor() {
    const parser = inject(ParserService);
    const auth = inject(AuthService);

    super(parser, auth);

    this.parser = parser;
    this.auth = auth;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.eventTypes = [];
    this.expandedDays = [];

    this._applyCachedView();

    this.breakpointObserver.observe(['(min-width: 576px)']).subscribe(result => {
      this.isCalendarOffCanvasAllowed = result.matches;
    });

    this.breakpointObserver.observe(['(max-width: 992px)']).subscribe(result => {
      if (result.matches) {
        // Fixing month view below large breakpoint
        this.setView(CalendarView.Month);
        // Variable to allow day click to show event info for mobile month view
        this.isCustomMonthEventViewForMobile = true;
      } else {
        this.isCustomMonthEventViewForMobile = false;
      }
    });

    const CALENDAR_RESPONSIVE = {
      small: {
        breakpoint: '(max-width: 576px)',
        daysInWeek: 2,
      },
      medium: {
        breakpoint: '(max-width: 768px)',
        daysInWeek: 3,
      },
      large: {
        breakpoint: '(max-width: 960px)',
        daysInWeek: 5,
      },
    };

    this.breakpointObserver
      .observe(Object.values(CALENDAR_RESPONSIVE).map(({ breakpoint }) => breakpoint))
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: BreakpointState) => {
        const foundBreakpoint = Object.values(CALENDAR_RESPONSIVE).find(
          ({ breakpoint }) => !!state.breakpoints[breakpoint],
        );
        if (foundBreakpoint) {
          this.daysInWeek = foundBreakpoint.daysInWeek;
        } else {
          this.daysInWeek = 7;
        }
        this.cdr.markForCheck();
      });

    this.fetchEvents();
  }

  fetchEvents(): void {
    const getStart: any = {
      month: startOfMonth,
      week: startOfWeek,
      day: startOfDay,
    }[this.view];

    const getEnd: any = {
      month: endOfMonth,
      week: endOfWeek,
      day: endOfDay,
    }[this.view];

    setTimeout(() => {
      this.events$ = this.cache
        .getListThroughCache('calendar/events', {
          calendars: JSON.stringify(this.getCalendar()),
          startDate: getUnixTime(this.toGMT(getStart(this.viewDate))),
          endDate: getUnixTime(this.toGMT(getEnd(this.viewDate))),
        })
        .pipe(
          map((results: CalendarEvent[]) => {
            return results.map((event: any | CalendarEvent) => {
              const country: CampusType = this.getCalendars().filter(
                (country: CampusType): boolean =>
                  event.calendar === (country.calendars?.length ? country.calendars[0].calendar : undefined),
              )[0];
              return {
                start: new Date(event.start),
                end: new Date(event.end),
                title: !event.summary.includes(country?.name || '')
                  ? (country?.name || '') + ' ' + event.summary
                  : event.summary,
                description: event.description,
                color: colors[country?.type || 'event'],
                allDay: event.start.length < 11,
                type: event.summary.includes('Holiday') ? 'holiday' : 'event',
              };
            });
          }),
          tap((results: CalendarEvent[]): void => {
            // Initialise an array of unique event types, starting with 'Events'.
            // Add 'Holidays' from the results array if they are present.
            const uniqueEventTypes: EventType[] = [
              { type: 'Events', active: true },
              // Filter the results to only include events with a title that starts with 'Holiday',
              // then map them to an array of objects with {type: 'Holidays', active: true}.
              ...results
                .filter((event: CalendarAppEvent): boolean => event.title.split(':')[0] === 'Holiday')
                .map((): EventType => ({
                  type: 'Holidays',
                  active: true,
                })),
            ];
            // Check if all unique event types in `uniqueEventTypes` are already present in `this.eventTypes`.
            if (
              !uniqueEventTypes.every((uniqueType: EventType) =>
                this.eventTypes.some(
                  // Check if the current unique type is already in this.eventTypes
                  (existingType: EventType): boolean => existingType.type === uniqueType.type,
                ),
              )
            ) {
              // If not all unique types are present, update this.eventTypes.
              // Use a Map to deduplicate the event types based on their 'type' property.
              this.eventTypes = Array.from(
                uniqueEventTypes.reduce((acc: Map<string, EventType>, cur: EventType) => {
                  acc.set(cur.type, cur); // Use the 'type' as the key to ensure uniqueness.
                  return acc;
                }, new Map<string, EventType>()),
                // Convert the Map back to an array of unique EventType objects.
              ).map(([key, value]) => value);
            }
            this.cdr.markForCheck();
          }),
          // Filter event types
          map((results: CalendarAppEvent[]): CalendarAppEvent[] => {
            return results.filter((event: CalendarAppEvent) => {
              const eventType = event.title.split(':')[0] === 'Holiday' ? 'Holidays' : 'Events';
              return this.eventTypes.some((type: EventType) => type.type === eventType && type.active);
            });
          }),
        );
    }, 0);
  }

  private toGMT(date: Date): Date {
    const dateCopy = new Date(date.getTime());
    const offset = dateCopy.getTimezoneOffset();
    dateCopy.setMinutes(dateCopy.getMinutes() - offset);
    return dateCopy;
  }

  handleEvent(action: string, event: CalendarAppEvent): void {
    this.modalData = { event, action };
    this.openEventModal();
  }

  openEventModal(): void {
    this.modalRef = this.modalService.show(this.eventModal!);
  }

  closeEventModal(): void {
    this.modalRef?.hide();
  }

  dayClicked({ date, events }: { date: Date; events: CalendarAppEvent[] }): void {
    if (!this.isCustomMonthEventViewForMobile) {
      if (isSameMonth(date, this.viewDate)) {
        this.viewDate = date;
      }
    } else {
      if ((isSameDay(this.viewDate, date) && this.activeDayIsOpen) || events.length === 0) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
        this.viewDate = date;
      }
    }
  }

  closeMobileEventPanel(): void {
    this.activeDayIsOpen = !this.activeDayIsOpen;
  }

  setView(view: CalendarView) {
    this.view = view;
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.destroy$.next();
  }

  calendarOffCanvasToggle(status: boolean) {
    if (status && this.isCalendarOffCanvasAllowed) {
      const spaceBetweenSidebarAndCalendar = 15; // Gap between sidebar and calendar when calendar pushed off canvas
      this.toggleCalendarOffCanvas = true;
      document.documentElement.style.setProperty(
        '--calendar-sidenav-width',
        `${this.sidebarElWidth! - spaceBetweenSidebarAndCalendar}px`,
      );
    } else {
      this.toggleCalendarOffCanvas = false;
      document.documentElement.style.setProperty('--calendar-sidenav-width', `0`);
    }
  }

  offCanvasToggle() {
    this.sidebarToggleStatus = !this.sidebarToggleStatus;
    this.calendarOffCanvasToggle(this.sidebarToggleStatus);
  }

  getCalendar(): any[] {
    const enabledCampuses = this.campus.getEnabledCampuses();
    const currentCampusId = this.campus.selectedCampus;

    // Add safety checks
    if (!enabledCampuses || enabledCampuses.length === 0 || !currentCampusId) {
      console.warn('No enabled campuses or current campus ID available');
      return [];
    }

    // Convert to number to ensure proper comparison
    const currentCampusIdNumber = Number(currentCampusId);

    // Find the campus by ID/zone instead of using array index
    const currentCampus = enabledCampuses.find(
      campus => Number(campus.id) === currentCampusIdNumber || Number(campus.zone) === currentCampusIdNumber,
    );

    if (!currentCampus) {
      console.warn(`Campus with ID ${currentCampusIdNumber} not found in enabled campuses:`, enabledCampuses);
      return [];
    }

    return [currentCampus]
      .filter(
        (campus: any) => (campus.calendars ?? []).filter((calendar: any) => calendar.type === this.calendarType).length,
      )
      .map((campus: CampusType) => {
        const matchingCalendar = (
          (campus.calendars ?? []).filter((calendar: any) => calendar.type === this.calendarType) as any
        )[0];

        return { calendar: matchingCalendar?.calendar };
      });
  }

  private getCalendars(): (any | CampusType)[] {
    return [...this.campus.getEnabledCampuses()].filter(
      (calendar: any) => (calendar.calendars ?? []).filter((c: any) => c.type === this.calendarType).length,
    );
  }

  private _getCacheKey(): string {
    return [this.block.uid, 'block-calendar'].join('-');
  }

  private _applyCachedView() {
    const cacheValue = this.cache.getFromStorage(this._getCacheKey());
    if (!cacheValue) {
      return;
    }

    const cachedView = JSON.parse(cacheValue);
    const calendars: CampusType[] = this.getCalendar();
    const currentCampus = this.campus.getCurrentCampus();

    if (cachedView && Array.isArray(cachedView) && currentCampus) {
      calendars
        .filter((country: CampusType): boolean => country.code !== currentCampus.code)
        .forEach((country: CampusType & { selected?: boolean }): void => {
          if (country.calendars && cachedView.includes(country.calendars[0].calendar)) {
            country.selected = true;
          }
        });
      // Set 'View All' input to checked if all calendars are selected in storage
      this.isAllToggled = cachedView.length === calendars.length;
    }
  }

  expandDay(day: WeekDay): void {
    if (!this.isExpanded(day)) {
      this.expandedDays.push(day.date.getTime());
    }
  }

  isExpanded(day: WeekDay): boolean {
    return this.expandedDays.includes(day.date.getTime());
  }
}
